import { createContext, useContext } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'

import API from '@shared/services/src/API'
import { allDeserialized } from '@shared/services/src/Lookup.mocks'
import QK from '@shared/utils/src/QueryHelper/QueryKeys'

/**
 * Provide a context for all dropdown options.
 */
export default function DropdownOptionsProvider({ children }) {
  const { data } = useSuspenseQuery({
    queryKey: QK.platform.constants.list,
    queryFn: () => API.platform.constants.list(),
    select,
  })

  return <DropdownOptionsContext.Provider value={data}>{children}</DropdownOptionsContext.Provider>
}

export function DropdownOptionsMockedProvider({ children }) {
  return <DropdownOptionsContext.Provider value={select(allDeserialized)}>{children}</DropdownOptionsContext.Provider>
}

export const DropdownOptionsContext = createContext(undefined)

export const useDropdownOptions = () => useContext(DropdownOptionsContext)

/**
 * Return a value for the given key
 *
 * @example:
 * const data = useLookup('key')
 *
 * @param key - The dropdown options key
 * @param select - A function to map the value, like in react-query
 * @returns {*} - The dropdown options value
 */
export const useLookup = (key, select) => {
  const value = useDropdownOptions()?.[key]
  if (select) return select(value)
  return value
}

export const objectify = (value) => value.reduce((acc, { key, label }) => ({ ...acc, [key]: label }), {})

export const Lookup = {
  Languages: 'languages',
  Sexualities: 'patient_sexualities',
  StatusChangingReasons: 'reasons_for_changing_status',
  NoLongerInterestedReasons: 'no_longer_interested',
  IncompleteApptReasons: 'incomplete_appt_reasons',
  TechnicalIssuesReasons: 'technical_issues_reasons',
  StiTestingTechnicalIssuesReasons: 'sti_testing_technical_issues_reasons',
  AsyncTechnicalIssuesReasons: 'async_technical_issues_reasons',
  IneligibleApptReasons: 'ineligible_appt_reasons',
  ProviderSuffixes: 'provider_suffixes',
  GenderList: 'patient_genders',
  RaceList: 'patient_races',
  RelationshipList: 'patient_relationships',
  EthnicityList: 'patient_ethnicities',
  PronounsList: 'patient_pronouns',
  SexPartners: 'sex_partners',
  SexType: 'sex_type',
  SexAmount: 'sex_amount',
  HadDiseases: 'had_diseases',
  HadSTD: 'had_std',
  SexList: 'born_as',
  Timezones: 'timezones',
  EncounterTypes: 'encounter_types',
  LabVendors: 'lab_vendors',
  ProviderTypes: 'provider_types',
  ServiceLines: 'service_lines',
  AssessmentTypes: 'assessment_types',
  ActivityEventCategories: 'patient_event_categories',
  MATasks: 'ma_tasks',
  TasksLHC: 'lhc_tasks',
  CSTasks: 'cs_tasks',
  AdminTasks: 'admin_tasks',
  CBOEscalationTasks: 'cbo_escalation_tasks',
  MAEscalationTasks: 'ma_escalation_tasks',
  SupportEscalationTasks: 'support_escalation_tasks',
  ProviderEscalationTasks: 'provider_escalation_tasks',
  ProviderTasks: 'provider_tasks',
  PARTasks: 'par_tasks',
  ProviderTasksForMA: 'provider_tasks_for_ma',
  PharmacyTasks: 'pharmacy_tasks',
  RXRequiredTasks: 'rx_required_tasks',
  TasksOptionalPatient: 'without_patient_tasks',
  TasksPreventManualCreation: 'tasks_prevent_manual_creation',
  QLinkTaskPanels: 'task_qlink_quest_panels',
  IncompleteLabDeclineReasons: 'reasons_for_returning_incomplete_labs',
  SOPCategories: 'sop_categories',
  SOPPolicies: 'sop_policies',
  TaskExpiredReasons: 'task_expired_reasons',
  ProviderTaskExpiredReasons: 'provider_task_expired_reasons',
  PriorAuthDoneReasons: 'prior_auth_done_reasons',
  ImmediateAccessReasons: 'immediate_access_reasons',
  GileadTaskDoneReasons: 'gilead_done_reasons',
  AppealTaskDoneReasons: 'appeal_done_reasons',
  PAFDoneReasons: 'paf_done_reasons',
  LabStatuses: 'lab_statuses',
  LabVitals: 'lab_vitals',
  PrescriptionStatuses: 'prescription_statuses',
  RXStatusesForPharmacy: 'rx_statuses_for_pharmacy',
  LabReviewStatuses: 'lab_review_statuses',
  ReasonsPendingQuestOrder: 'reasons_pending_quest_order',
  ReasonsForMedication: 'soap_reasons_for_medications',
  PriorAuthTemplates: 'prior_auth_templates',
  PatientAtRiskDoneReasons: 'par_done_reasons',
  PEPPatientAtRiskDoneReasons: 'pep_par_done_reasons',
  HIVPatientAtRiskDoneReasons: 'hiv_par_done_reasons',
  GLP1PatientAtRiskDoneReasons: 'glp1_par_done_reasons',
  RenewingLicenseReasons: 'renewing_license_reasons',
  SoapLabResultsPositiveForSti: 'soap_lab_results_positive_for_sti',
  AppointmentCommunicationTypes: 'appointment_communication_types',
  MorbiditySyphilisStage: 'doh_morbidity_syphilis_stage',
  MorbiditySymptomsSigns: 'doh_morbidity_symptoms_signs',
  RoiMedicalRecordsEntities: 'roi_medical_records_entities',
  LabFollowUpTaskResolveReasons: 'lab_follow_up_task_resolve_reasons',
  ReactLATeamMembers: 'reach_la_team_members',
  ExpireGAReasons: 'expire_ga_reasons',
  ProviderReassignmentReasons: 'provider_reassignment_reasons',
  ProviderStatuses: 'provider_statuses',
  BreakGlassReasons: 'break_glass_reasons',
  EscalationReasons: 'escalation_task_reasons',
}

const select = (data) => {
  return data.reduce((acc, curr) => {
    if (
      [Lookup.ProviderTypes, Lookup.ServiceLines, Lookup.RXStatusesForPharmacy, Lookup.LabReviewStatuses, Lookup.LabVendors].includes(
        curr.key
      )
    ) {
      acc[curr.key] = curr.value.reduce((acc, [id, value]) => ({ ...acc, [value]: id }), {})
    } else {
      acc[curr.key] = curr.value
    }
    return acc
  }, {})
}
